
import { IPublicClientApplication, PublicClientApplication, InteractionType, AccountInfo } from '@azure/msal-browser';
import {
  MsalGuard, MsalInterceptor, MsalBroadcastService, MsalInterceptorConfiguration, MsalService,
  MSAL_GUARD_CONFIG, MSAL_INSTANCE, MSAL_INTERCEPTOR_CONFIG, MsalGuardConfiguration, MsalRedirectComponent, ProtectedResourceScopes
} from '@azure/msal-angular';
import { msalConfig, loginRequest, protectedResources } from './auth-config';
import { BrowserModule } from '@angular/platform-browser';
import { APP_INITIALIZER, CUSTOM_ELEMENTS_SCHEMA, ErrorHandler, LOCALE_ID, NgModule } from '@angular/core';
import { AppRoutingModule } from './app-routing.module';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { AppComponent } from './app.component';
import { ProfileComponent } from './modules/profile/profile.component';
import { environment } from 'src/environments/environment';
import { MaterialModule } from './shared/material/material.module';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { HttpClient, HttpClientModule, HTTP_INTERCEPTORS } from '@angular/common/http';
import { TranslateLoaderService } from './core/services/internationalization/translate-loader.service';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { CalculatorComponent } from './modules/calculator/calculator.component';
import { CoreModule } from './core/core.module';
import { SharedModule } from './shared/shared.module';
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE } from '@angular/material/core';
import { MAT_MOMENT_DATE_ADAPTER_OPTIONS, MomentDateAdapter } from '@angular/material-moment-adapter';
import { ContractsComponent } from './modules/contracts/contracts.component';
import { ClientContractComponent } from './modules/client-contract/client-contract.component';
import { PolicyListComponent } from './modules/contracts/policy-list/policy-list.component';
import { PolicyListItemComponent } from './modules/contracts/policy-list/policy-list-item/policy-list-item.component';
import { PolicyListGroupComponent } from './modules/contracts/policy-list/policy-list-group/policy-list-group.component';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import { StoreModule } from '@ngrx/store';
import { coveragesReducers } from './state/coverages/coverages.reducers';
import { IAppState } from './state/app.state';
import { objectsReducers } from './state/objects/objects.reducers';
import { quotationReducers } from './state/quotation/quotation.reducers';
import { FetchDataComponent } from './fetch-data/fetch-data.component';
import { Injector } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { LOCATION_INITIALIZED } from '@angular/common';
import { userReducers } from './state/user/user.reducers';
import { policyReducers } from './state/policy/policy.reducers';
import { LandingComponent } from './modules/landing/landing.component';
import { clientReducers } from './state/client/client.reducers';
import { contractsReducers } from './state/contracts/contracts.reducers';
import { paymentFractionsReducers } from './state/paymentFractions/paymentFractions.reducers';
import { MsalAuthInterceptor } from './core/interceptors/msal-auth-interceptor.service';
import { EncryptDecryptAuthInterceptor } from './core/interceptors/encrypt-decrypt-auth.interceptor';
import { ErrorHandlerService } from './core/services/errorhandling/error-handler.service';
import { LoggingService } from './core/services/logging/logging.service';
import { SuperGuaranteeComponent } from './modules/super-guarantee/super-guarantee.component';
import { ReactiveFormsModule } from '@angular/forms';

const isIE = window.navigator.userAgent.indexOf('MSIE ') > -1 || window.navigator.userAgent.indexOf('Trident/') > -1;
const interceptors = [
  {
    provide: HTTP_INTERCEPTORS,
    useClass: MsalAuthInterceptor,
    multi: true
  },
  {
    provide: HTTP_INTERCEPTORS,
    useClass: EncryptDecryptAuthInterceptor,
    multi: true
  },
  {
      provide: HTTP_INTERCEPTORS,
      useClass: MsalInterceptor,
      multi: true
    },
    {
      provide: MSAL_INSTANCE,
      useFactory: MSALInstanceFactory
    },
    {
      provide: MSAL_GUARD_CONFIG,
      useFactory: MSALGuardConfigFactory
    },
    {
      provide: MSAL_INTERCEPTOR_CONFIG,
      useFactory: MSALInterceptorConfigFactory
    },
   
    MsalService,
    MsalGuard,
    MsalBroadcastService 
]

@NgModule({
  declarations: [
    AppComponent,
    FetchDataComponent,
    ProfileComponent,
    CalculatorComponent,
    ContractsComponent,
    ClientContractComponent,
    PolicyListComponent,
    PolicyListItemComponent,
    PolicyListGroupComponent,
    LandingComponent,
    SuperGuaranteeComponent,
  ],
  imports: [
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useClass: TranslateLoaderService,
        deps: [HttpClient]
      }
    }),
    MaterialModule,
    BrowserModule.withServerTransition({ appId: 'ng-cli-universal' }),
    HttpClientModule,
    AppRoutingModule,
    ReactiveFormsModule,
    CoreModule,
    BrowserAnimationsModule,
    SharedModule,
 
    // Bind state reducers
    StoreModule.forRoot<IAppState>({
      coverages: coveragesReducers,
      objects: objectsReducers,
      quotation: quotationReducers,
      user: userReducers,
      policy: policyReducers,
      client: clientReducers,
      contracts: contractsReducers,
      paymentFractions: paymentFractionsReducers
    }),

    // Instrumentation must be imported after importing StoreModule (config is optional)
    StoreDevtoolsModule.instrument({
      maxAge: 25, // Retains last 25 states
      logOnly: environment.production, // Restrict extension to log-only mode
    }),
  ],
  providers: [
    MsalGuard,
    // The locale would typically be provided on the root module of your application. We do it at
    // the component level here, due to limitations of our example generation script.
    { provide: MAT_DATE_LOCALE, useValue: 'es-ES' },

    // `MomentDateAdapter` and `MAT_MOMENT_DATE_FORMATS` can be automatically provided by importing
    // `MatMomentDateModule` in your applications root module. We provide it at the component level
    // here, due to limitations of our example generation script.
    {
      provide: DateAdapter,
      useClass: MomentDateAdapter,
      deps: [MAT_DATE_LOCALE, MAT_MOMENT_DATE_ADAPTER_OPTIONS]
    },
    { provide: ErrorHandler, useClass: ErrorHandlerService },
    LoggingService,
    {
      provide: MAT_DATE_FORMATS, useValue: {
        parse: {
          dateInput: "L",
        },
        display: {
          dateInput: "L",
          monthYearLabel: "MMM YYYY",
          dateA11yLabel: "LL",
          monthYearA11yLabel: "MMMM YYYY",
        },
      }
    },
    { provide: LOCALE_ID, useValue: 'es-ES' },
    {
      provide: APP_INITIALIZER,
      useFactory: appInitializerFactory,
      deps: [TranslateService, Injector],
      multi: true
    },
    ...interceptors,
  ],
  bootstrap: [AppComponent, MsalRedirectComponent], // MsalRedirectComponent bootstrapped here
  schemas: [CUSTOM_ELEMENTS_SCHEMA]
})


export class AppModule { }

export function HttpLoaderFactory(http: HttpClient) {
  return new TranslateHttpLoader(http);
}



export function appInitializerFactory(translateService: TranslateService, injector: Injector) {
  return () => new Promise<any>((resolve: any) => {

    // init location
    const locationInitialized = injector.get(LOCATION_INITIALIZED, Promise.resolve(null));

    locationInitialized.then(() => {

      let default_lang = environment.lang;

      translateService.addLangs(TranslateLoaderService.langs);

      if (environment.langSource === "web") {
        const userLang = translateService.getBrowserLang() || navigator.language;
        if (userLang && TranslateLoaderService.langs.includes(userLang)) {
          default_lang = userLang;
        }
      }

      translateService.setDefaultLang(default_lang);

      translateService.use(default_lang).subscribe(() => {
        console.info(`Successfully initialized '${default_lang}' language.'`);
      }, err => {
        console.error(`Problem with '${default_lang}' language initialization.'`);
      }, () => {
        resolve(null);
      });
    });
  });
}

//const isIE = window.navigator.userAgent.indexOf('MSIE ') > -1 || window.navigator.userAgent.indexOf('Trident/') > -1;
//const initializer = (pwaService: PwaService) => () => pwaService.initPwaPrompt();
/**
 * Here we pass the configuration parameters to create an MSAL instance.
 * For more info, visit: https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-angular/docs/v2-docs/configuration.md
 */


export function MSALInstanceFactory(): IPublicClientApplication {
  return new PublicClientApplication(msalConfig);
}

export function MSALInterceptorConfigFactory(): MsalInterceptorConfiguration {
  const protectedResourceMap = new Map<string, Array<string | ProtectedResourceScopes> | null>();

  protectedResourceMap.set(protectedResources.apicontroller.endpoint, [
    {
      httpMethod: 'GET',
      scopes: [environment.endpointwebapi]
    },
    {
      httpMethod: 'POST',
      scopes: [environment.endpointwebapi]
    }
  ]);
  return {
    interactionType: InteractionType.Redirect,
    protectedResourceMap,
  };
}

export function MSALGuardConfigFactory(): MsalGuardConfiguration {
  return {
    interactionType: InteractionType.Redirect,
    authRequest: loginRequest
  };
}